import { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useMemberDataSource } from 'hooks/dataSource/useMemberDataSource'
import { useAppDispatch } from 'states/hooks'
import { EntityType } from '@crew/enums/domain'
import { EventRecordingTranscribe } from '@crew/models/domain'
import { useTranslation } from '@crew/modules/i18n'
import { ValidateRules } from '@crew/utils/form'

import { useBulkUpdateEventRecordingTranscribesSpeakersMutation } from '@crew/apis/eventRecordingTranscribe/eventRecordingTranscribeApis'
import { BulkUpdateEventRecordingTranscribesSpeakersRequest } from '@crew/apis/eventRecordingTranscribe/models/bulkUpdateEventRecordingTranscribesSpeakers/request'
import {
  notifyEventRecordingTranscribeEvent,
  ObjectEventMessage,
} from 'features/app/states/appSlice'
import { NotifyEventType } from 'enums/app'
import { EditEventRecordingTranscribe } from './editEventRecordingTranscribesEntryForm'
import { useValueChangeEffect } from '@crew/hooks'

export type FormValues = {
  speakerId: string | null
  speakerName: string | null
}

export const useEditEventRecordingTranscribesEntryForm = (
  projectId: string | undefined
) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const speakerDataSource = useMemberDataSource(EntityType.Project, projectId)

  // react-hook-formの各種データを取得
  const { control, formState, handleSubmit, setValue, getValues, clearErrors } =
    useForm<FormValues>({
      criteriaMode: 'all',
    })

  // 発言者の入力チェック
  const validateSpeaker = useCallback(() => {
    const speakerId = getValues('speakerId')
    const speakerName = getValues('speakerName')

    // フロントエンドでは発言者IDと発言者名の両方が未入力の場合のみをチェックする
    // - バックエンドでは発言者ID・発信者名のいずれかが必ず入力されていることをチェックする
    if (!speakerId && !speakerName) {
      return false
    }

    clearErrors('speakerId')
    return true
  }, [clearErrors, getValues])

  const validateRules: ValidateRules<FormValues> = useMemo(
    () => ({
      speakerId: {
        validate: {
          always: () => validateSpeaker() || t('message.general.required'),
        },
      },
      speakerName: {},
    }),
    [t, validateSpeaker]
  )

  // 話者一括更新APIへのリクエスト処理を取得
  const [
    bulkUpdateEventRecordingTranscribesSpeakers,
    { isLoading: isLoadingBulkUpdateEventRecordingTranscribes },
  ] = useBulkUpdateEventRecordingTranscribesSpeakersMutation()

  // 話者一括更新処理
  const updateEventRecordingTranscribesSpeakers = useCallback(
    async (
      eventRecordingTranscribes: EditEventRecordingTranscribe[],
      formValues: FormValues
    ) => {
      // 処理対象がない場合は処理を中断する（UI側でもボタンを押せないようにはしているが念のため）
      if (eventRecordingTranscribes.length === 0) {
        return
      }

      const transcribes = eventRecordingTranscribes.map((transcribe) => {
        return {
          id: transcribe.eventRecordingTranscribeId,
          version: transcribe.version,
        }
      })

      const params: BulkUpdateEventRecordingTranscribesSpeakersRequest = {
        eventRecordingTranscribes: transcribes,
        speakerId: formValues.speakerId ?? undefined,
        speakerName: formValues.speakerName ?? undefined,
      }

      // 話者一括更新APIを呼び出す
      await bulkUpdateEventRecordingTranscribesSpeakers(params).unwrap()

      const objectEventMessage: ObjectEventMessage<EventRecordingTranscribe> = {
        eventType: NotifyEventType.Updated,
        id: eventRecordingTranscribes[0].eventRecordingTranscribeId,
        object: undefined,
      }
      dispatch(notifyEventRecordingTranscribeEvent(objectEventMessage))
    },
    [bulkUpdateEventRecordingTranscribesSpeakers, dispatch]
  )

  // データソースが利用可能になったら最初の項目を自動選択
  useValueChangeEffect(
    () => {
      const initializeSpeaker = async () => {
        try {
          // データソースから話者リストの最初の項目を取得し、初期値として設定
          const speakers = await speakerDataSource.load()
          if (speakers && speakers.length > 0) {
            const firstItem = speakers[0]
            if (firstItem && firstItem.id) {
              // フォームに初期値として設定
              setValue('speakerId', firstItem.id)
            }
          }
        } catch (error) {
          // データ取得エラーが発生した場合でも初期化に失敗するだけなので、ログを出力して処理自体は継続する
          console.warn('Failed to initialize speaker:', error)
        }
      }

      // 初期化処理を実行
      initializeSpeaker()
    },
    [speakerDataSource, setValue],
    speakerDataSource
  )

  return {
    control,
    formState,
    handleSubmit,
    setValue,
    getValues,
    validateRules,
    speakerDataSource,
    updateEventRecordingTranscribesSpeakers,
    isLoadingBulkUpdateEventRecordingTranscribes,
  }
}
